import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Only redirect if not already on auth page
      if (!window.location.pathname.includes('/login') &&
          !window.location.pathname.includes('/register')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getCurrentUser: () => api.get('/auth/me'),
};

// User APIs
export const userAPI = {
  getProfile: (id) => api.get(`/users/${id}`),
  getLeaderboard: (params) => api.get('/users/leaderboard', { params }),
  getStats: (id) => api.get(`/users/${id}/stats`),
  searchUsers: (query, excludeFriends = true) => api.get('/users/search', { params: { q: query, excludeFriends } }),
  updateUsername: (data) => api.put('/users/me/username', data),
  updatePrivacySettings: (data) => api.put('/users/me/privacy', data),
};

// Table APIs
export const tableAPI = {
  getTables: (params) => api.get('/tables', { params }),
  getTableById: (id, params) => api.get(`/tables/${id}`, { params }),
  getTableLeaderboard: (id, params) => api.get(`/tables/${id}/leaderboard`, { params }),
  getNearbyTables: (params) => api.get('/tables/nearby', { params }),
  createTable: (data) => api.post('/tables', data),
  searchTables: (query, latitude, longitude) => api.get('/tables/search', {
    params: { query, latitude, longitude }
  }),
  searchLocations: (query) => api.get('/tables/search-locations', { params: { query } }),
};

// Game APIs
export const gameAPI = {
  getRecentGames: (params) => api.get('/games', { params }),
  getUserGames: (id, params) => api.get(`/games/user/${id}`, { params }),
  recordGame: (data) => api.post('/games', data),
};

// Friend APIs
export const friendAPI = {
  // Friend requests
  sendRequest: (data) => api.post('/friends/request', data),
  sendInvite: (data) => api.post('/friends/invite', data),
  getIncomingRequests: () => api.get('/friends/requests/incoming'),
  getOutgoingRequests: () => api.get('/friends/requests/outgoing'),
  acceptRequest: (friendshipId) => api.put(`/friends/request/${friendshipId}/accept`),
  declineRequest: (friendshipId) => api.put(`/friends/request/${friendshipId}/decline`),
  cancelRequest: (friendshipId) => api.delete(`/friends/request/${friendshipId}`),

  // Friends management
  getFriends: () => api.get('/friends'),
  removeFriend: (friendshipId) => api.delete(`/friends/${friendshipId}`),

  // Friend stats
  getFriendStats: (friendId) => api.get(`/friends/${friendId}/stats`),
  getHeadToHead: (friendId) => api.get(`/friends/${friendId}/head-to-head`),

  // Friendship status
  getFriendshipStatus: (userId) => api.get(`/friends/status/${userId}`),
};

// Team Game APIs
export const teamGameAPI = {
  logTeamGame: (data) => api.post('/team-games', data),
  getTeamStats: (player1Id, player2Id, params) =>
    api.get(`/team-games/stats/${player1Id}/${player2Id}`, { params }),
  getTeamGames: (player1Id, player2Id, params) =>
    api.get(`/team-games/games/${player1Id}/${player2Id}`, { params }),
  getUserTeamGames: (userId, params) =>
    api.get(`/team-games/user/${userId}`, { params }),
};

// Notification APIs
export const notificationAPI = {
  getNotifications: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
};

export default api;
